import { useEffect, useRef, useState } from 'react';
import { GetStaticProps } from 'next';
import { useRouter } from 'next/router';
import { useLazyQuery, useMutation } from '@apollo/client';
import { toast } from 'react-toastify';
import { useTranslations } from 'next-intl';

import {
  CheckoutCartProviderId,
  useEligibility,
} from '@monofront/business/contexts';
import {
  ATTACH_PAYMENT_METHOD,
  COMPLETE_ORDER,
  CONFIRM_FLOW_PAYMENT,
  GET_CHECKOUT_CART,
  GET_PAYMENT,
} from '@monofront/business/queries';
import { useSubscribeInCheckout } from '@monofront/business/hooks/checkout/useSubscribeInCheckout';
import HeartLoader from '@monofront/design-system/components/HeartLoader';
import {
  CardSavingMode,
  CheckoutStateEnum,
  ICompleteOrderQueryResult,
  IConfirmFlowPaymentQueryResult,
  IGetPaymentResult,
  MembershipPlan,
  PaymentMethodSource,
  PaymentMethodTypes,
} from '@monofront/types';

import LayoutModal from '@components/LayoutModal';
import {
  CKO_FLOW_CARD_SAVING_STORAGE_KEY,
  CKO_FLOW_TOKEN_VALUE_STORAGE_KEY,
} from '@components/PaymentForms/CheckoutComFlowForm';
import { useAuthentication } from '@context/businessContainer';
import withAuthentication from '@hocs/withAuthentication';
import { useShoppingCart } from '@hooks/cart/useShoppingCart';
import { MAAP_COUPON_STORAGE_KEY } from '@screens/CheckoutPayment/PaymentMethodsList/OptionCheckoutComCard';
import { runPostOrderSideEffects } from '@screens/CheckoutPayment/utils/runPostOrderSideEffects';
import { getRunningApp, getRunningPlatform } from '@utils/environment';
import { LFPage, injectLayoutData } from '@utils/layout';
import logger from '@utils/logger';

type ProcessingPageProps = {
  closeRedirection: string;
};

/**
 * Page that makes the order completion
 */
const ProcessingPage: {
  getLayout: () => void;
} & LFPage<ProcessingPageProps> = () => {
  const router = useRouter();
  const paymentId = router.query['cko-payment-id'] as string | undefined;

  // Nothing to complete without a payment id — someone landed here directly.
  useEffect(() => {
    if (router.isReady && !paymentId) {
      logger.error('no payment id to process');
      void router.replace('/home');
    }
  }, [router.isReady, paymentId]);

  // Block the redirections while the order is processing
  useEffect(() => {
    const processingPath = router.asPath;

    router.beforePopState(() => {
      void router.replace(processingPath);

      return false;
    });

    return () => {
      router.beforePopState(() => true);
    };
  }, [router]);

  if (!router.isReady || !paymentId) {
    return <HeartLoader />;
  }

  // lego passes the token as a query param; sessionStorage is the fallback for
  // sessions created before that change.
  const checkoutCartId =
    (router.query.checkoutCartId as string) ||
    sessionStorage.getItem(CKO_FLOW_TOKEN_VALUE_STORAGE_KEY) ||
    '';

  return (
    <CheckoutCartProviderId checkoutCartId={checkoutCartId}>
      <FlowCompletionHandler
        paymentId={paymentId}
        checkoutCartId={checkoutCartId}
      />
    </CheckoutCartProviderId>
  );
};

type FlowCompletionHandlerProps = {
  paymentId: string;
  checkoutCartId: string;
};

/**
 * Cap on the wait for `customer` and the shopping cart id: completing a paid
 * order matters more than the data derived from them.
 */
const HYDRATION_TIMEOUT_MS = 5000;

const FlowCompletionHandler: React.FC<FlowCompletionHandlerProps> = ({
  paymentId,
  checkoutCartId,
}) => {
  const router = useRouter();
  // Only Checkout.com's own redirect adds its session ids; the no-3DS path is our
  // router.push from payment.tsx. The two are otherwise identical in the logs.
  const returnLeg = router.query['cko-session-id'] ? 'cko_redirect' : 'no_3ds';
  const tGlobal = useTranslations('Global');
  const tCheckoutPaymentErrors = useTranslations('Checkout.Payment.Errors');
  const { isSubscriptionAvailable } = useSubscribeInCheckout(useAuthentication);
  const { customer } = useAuthentication();
  const { checkEligibility } = useEligibility();

  const [confirmFlowPayment] =
    useMutation<IConfirmFlowPaymentQueryResult>(CONFIRM_FLOW_PAYMENT);
  const [completeOrderMutation] =
    useMutation<ICompleteOrderQueryResult>(COMPLETE_ORDER);
  const [getCheckoutCart] = useLazyQuery(GET_CHECKOUT_CART);
  const [getPayment] = useLazyQuery<IGetPaymentResult>(GET_PAYMENT);
  const [attachPaymentMethod] = useMutation(ATTACH_PAYMENT_METHOD);
  const { deleteCurrentShoppingCart, shoppingCartId } = useShoppingCart();
  const hasStartedRef = useRef(false);
  const [hasWaitedForHydration, setHasWaitedForHydration] = useState(false);

  useEffect(() => {
    const timeout = setTimeout(
      () => setHasWaitedForHydration(true),
      HYDRATION_TIMEOUT_MS,
    );

    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    // true until timeout in progress or necessary data as been retrieved
    const waitForHydrationOrTimeout =
      ((!shoppingCartId || !customer) && !hasWaitedForHydration) ||
      hasStartedRef.current;

    if (waitForHydrationOrTimeout) return;
    hasStartedRef.current = true;

    const completeCheckoutAfterRedirect = async () => {
      try {
        logger.info('Completing checkout order after Flow payment', {
          payment_id: paymentId,
          checkout_cart_id: checkoutCartId,
          return_leg: returnLeg,
        });

        if (!customer) {
          logger.error(
            'Completing the Flow payment without the customer document — savings, order count and cart deletion will be wrong',
            {
              payment_id: paymentId,
              checkout_cart_id: checkoutCartId,
            },
          );
        }

        const { data: cartData } = await getCheckoutCart({
          variables: { tokenValue: checkoutCartId },
        });

        const cart = cartData?.getCheckoutCartWithRefresh?.cart;

        if (!cart?.payment?.id) {
          throw new Error('Cart or payment not found after the Flow payment');
        }

        // A remount (e.g. the customer hard-refreshing) after completeOrder
        // already ran: the in-memory hasStartedRef above doesn't survive that,
        // but the OMS cart's checkoutState does, and it's the same signal the
        // rest of checkout already treats as authoritative.
        if (cart.checkoutState === CheckoutStateEnum.COMPLETED) {
          logger.info('Flow payment already completed, skipping replay', {
            payment_id: paymentId,
            checkout_cart_id: checkoutCartId,
            return_leg: returnLeg,
          });

          await router.replace({
            pathname: '/checkout/confirmation',
            query: { checkoutCartId },
          });

          return;
        }

        const paymentMethodCode =
          cart.payment.method?.code || 'checkout_com_card';

        // Normally a no-op repair: lego already created the intent during
        // handleSubmit. It still runs, to cover a failed patch there and any
        // payment method whose submit Flow handled itself.
        await confirmFlowPayment({
          variables: {
            tokenValue: checkoutCartId,
            cartPaymentId: cart.payment.id,
            paymentMethodCode,
            financialPaymentIntentId: paymentId,
          },
        });

        const cardSaving = sessionStorage.getItem(
          CKO_FLOW_CARD_SAVING_STORAGE_KEY,
        ) as CardSavingMode | null;

        sessionStorage.removeItem(CKO_FLOW_CARD_SAVING_STORAGE_KEY);

        if (cardSaving && cardSaving !== CardSavingMode.NO_SAVE) {
          try {
            const { data: paymentData } = await getPayment({
              variables: { paymentId },
            });

            if (paymentData?.getPayment?.paymentMethodId) {
              await attachPaymentMethod({
                variables: {
                  paymentMethodId: paymentData.getPayment.paymentMethodId,
                  paymentMethodSource: PaymentMethodSource.CHECKOUT,
                  financialPaymentId: paymentId,
                },
              });
            }
          } catch (error) {
            logger.info('Card attachment skipped or failed', { error });
          }
        }

        const maapCoupon = localStorage.getItem(MAAP_COUPON_STORAGE_KEY);

        localStorage.removeItem(MAAP_COUPON_STORAGE_KEY);

        await completeOrderMutation({
          variables: {
            tokenValue: checkoutCartId,
            paymentMetadata: {
              app: getRunningApp(),
              platform: getRunningPlatform(),
            },
            ...(isSubscriptionAvailable && {
              withSubscription: true,
              membershipPlan: MembershipPlan.YEARLY,
              subscriptionPromotionCode: maapCoupon || undefined,
            }),
          },
        });
        await deleteCurrentShoppingCart();

        if (cart.shippingAddress) {
          checkEligibility({
            street: cart.shippingAddress.street,
            city: cart.shippingAddress.city,
            postalCode: cart.shippingAddress.postcode,
            country: cart.shippingAddress.countryCode,
          });
        }

        const totalEconomy = (cart.items || []).reduce(
          (acc: number, { variantOriginalPrice, unitPrice, quantity }: any) =>
            variantOriginalPrice > 0
              ? acc + ((variantOriginalPrice - unitPrice) * quantity) / 100
              : acc,
          0,
        );

        const postOrderResult = runPostOrderSideEffects({
          checkoutCart: cart,
          customer,
          totalEconomy,
          isSubscriptionAvailable,
          selectedPaymentType: PaymentMethodTypes.CARD,
        });

        await router.replace({
          pathname: '/checkout/confirmation',
          query: {
            checkoutCartId,
            pastSavingsAmount: postOrderResult.pastSavingsAmount,
            newSavingsAmount: postOrderResult.newSavingsAmount,
            isFirstOrder: postOrderResult.isFirstOrder,
            'cs-free-trial-start': postOrderResult.isFreeTrialStart,
          },
        });
      } catch (error: any) {
        logger.error('Failed to complete checkout after the Flow payment', {
          error,
          payment_id: paymentId,
          checkout_cart_id: checkoutCartId,
          return_leg: returnLeg,
        });

        // redirects to the cart page with an explicative toast when an OOS is detected at the complete order
        const isOutOfStock =
          error?.graphQLErrors?.[0]?.code === 'ORDER_OUT_OF_STOCK';

        toast.error(
          isOutOfStock
            ? tCheckoutPaymentErrors('cannotCompleteOrderOutOfStock')
            : tGlobal('errorOccurredTryAgain'),
        );
        await router.replace('/cart');
      }
    };

    void completeCheckoutAfterRedirect();
  }, [
    paymentId,
    customer?.uid,
    shoppingCartId,
    isSubscriptionAvailable,
    hasWaitedForHydration,
  ]);

  return (
    <div className="tw-flex tw-min-h-[300px] tw-items-center tw-justify-center">
      <HeartLoader />
    </div>
  );
};

ProcessingPage.getLayout = () => LayoutModal;

export default withAuthentication(ProcessingPage);

export const getStaticProps = injectLayoutData<
  ProcessingPageProps,
  GetStaticProps<ProcessingPageProps>
>(async () => ({
  props: {
    closeRedirection: '/home',
  },
  revalidate: 1800,
}));
